import { FileStatus } from 'constants/file-status.constants';
import {
  downloadEditFileService,
  downloadFileService,
  fileUpload,
  IDataFeedJob,
  IGetCountryListProps,
} from 'services';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

type IFileStatus =
  | FileStatus.Error
  | FileStatus.Pending
  | FileStatus.Uploaded
  | FileStatus.Validated;
interface IFileState {
  files: {
    name: string;
    status: IFileStatus;
    errorMessage?: string | object;
  };
  fileUploadProps: IGetCountryListProps;
  selectedCategory: string;
  selectedDataFeed: IDataFeedJob | null;
  newReportID: number | null | string;
  fileHasErrors: boolean;
  isFileValid: boolean;
  addFile: (file: any) => any;
  deleteFile: () => void;
  resetFileUpload: () => void;
  updateSelectedCategory: (category: string) => void;
  updateSelectedDataFeed: (job: IDataFeedJob | null) => void;
  resetSelectedCategory: () => void;
  updateFileHasErrorsState: (value: boolean) => void;
  updateExistingErrors: (data: any) => void;
  setWasValidatedOnce: (value: boolean) => void;
  updateFileReportId: (reportId: number) => void;
  updateCountryCode: (code: string) => void;
  // updateFileStatus: (fileName: string, status: 'uploaded' | 'error', errorMessage?: string) => void;
}

interface IFileDownloadState {
  file: Blob | MediaSource;
  downloadFile: (countryCode: string, templateName: string) => void;
  downloadFileToEdit: (reportId: number) => void;
}

export const useFileStore = create<IFileState, [['zustand/persist', unknown]]>(
  persist(
    (set, get) => ({
      files: {
        name: '',
        status: FileStatus.Pending,
        errorMessage: '',
      },
      isFileValid: false,
      fileHasErrors: false,
      selectedCategory: '',
      selectedDataFeed: null,
      newReportID: null,
      fileUploadProps: {
        reportName: '',
        countryCode: '',
        reportId: '',
        fileName: '',
        extension: '',
        file: new Blob(),
        isValid: false,
      },
      updateSelectedCategory: (category: string) => set({ selectedCategory: category }),
      updateSelectedDataFeed: (job: IDataFeedJob | null) => set({ selectedDataFeed: job }),
      setWasValidatedOnce: (value: boolean) => set({ isFileValid: value }),
      addFile: async (fileProps: IGetCountryListProps) => {
        const { fileName, isValid } = fileProps;
        const prevData = get().fileUploadProps;
        if (!fileProps?.countryCode) {
          fileProps.countryCode = prevData?.countryCode;
        }
        set({ fileUploadProps: { ...prevData, ...fileProps } });

        const res: any = await fileUpload(fileProps);
        if (res instanceof Error) {
          set({
            files: { name: fileName, status: FileStatus.Error, errorMessage: res.message },
          });
          return;
        }
        if (res) {
          if (res.responseCode === 200) {
            if (!isValid) {
              set({ files: { name: fileName, status: FileStatus.Validated } });
            } else {
              set({ files: { name: fileName, status: FileStatus.Uploaded } });
              set({ newReportID: res.data.reportId });
              set({ fileUploadProps: { ...prevData, reportId: res.data.reportId } });
            }
          } else if (res.responseCode === 400) {
            set({
              files: {
                name: fileName,
                status: FileStatus.Error,
                errorMessage: res.responseMessage,
              },
            });
          } else if (res.responseCode === 422) {
            set({
              files: {
                name: fileName,
                status: FileStatus.Error,
                errorMessage: {
                  message: res.responseMessage,
                  list: res.data?.missingHeaders || [],
                },
              },
            });
          } else if (res.responseCode === 408) {
            set({
              files: {
                name: fileName,
                status: FileStatus.Error,
                errorMessage: res.responseMessage,
              },
            });
          } else if (res.responseCode === 500) {
            set({
              files: {
                name: fileName,
                status: FileStatus.Error,
                errorMessage: res.responseMessage,
              },
            });
          } else {
            set({
              files: { name: fileName, status: FileStatus.Error, errorMessage: 'Unknown Error' },
            });
          }
        } else {
          set({
            files: { name: fileName, status: FileStatus.Error, errorMessage: 'Unknown Error' },
          });
        }
        return res;
      },
      deleteFile: () => set({ files: { name: '', status: FileStatus.Pending } }),
      resetFileUpload: () => set({ files: { name: '', status: FileStatus.Pending } }),
      updateFileHasErrorsState: (value: boolean) => set({ fileHasErrors: value }),
      resetSelectedCategory: () => set({ selectedCategory: '', selectedDataFeed: null }),
      updateExistingErrors: (data: any) => {
        const prevFileProps = get().fileUploadProps;
        set({
          fileUploadProps: { ...prevFileProps, isValid: false },
        });
        set({
          files: {
            name: ' ',
            status: FileStatus.Error,
            errorMessage: {
              message: data?.responseMessage,
              list: data.list,
            },
          },
        });
      },
      updateFileReportId: (reportId: number) => {
        const prevData = get().fileUploadProps;
        set({ fileUploadProps: { ...prevData, reportId: reportId } });
      },
      updateCountryCode: (code: string) => {
        const prevData = get().fileUploadProps;
        set({ fileUploadProps: { ...prevData, countryCode: code } });
      },
    }),
    {
      name: 'kf1-fileStore', // name of the item in the storage (must be unique)
      storage: createJSONStorage(() => window.sessionStorage), // (optional) by default, 'localStorage' is used
    },
  ),
);

export const useFileDownloadStore = create<IFileDownloadState, [['zustand/persist', unknown]]>(
  persist(
    (set) => ({
      file: new Blob(),
      downloadFile: async (countryCode: string, templateName: string) => {
        const res: any = await downloadFileService(countryCode, templateName);
        set({ file: res });
      },
      downloadFileToEdit: async (reportId: number) => {
        const res: any = await downloadEditFileService(reportId);
        set({ file: res });
      },
    }),
    {
      name: 'kf1-downloadfileStore', // name of the item in the storage (must be unique)
      storage: createJSONStorage(() => window.sessionStorage), // (optional) by default, 'localStorage' is used
    },
  ),
);
